import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, LogOut, Calendar as CalIcon, Users, Mail, RefreshCcw, Download, Hourglass } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { CLASSES, IMAGES } from "@/data/classes";

const DEFAULT_FORM = {
  class_slug: "bls",
  date: "",
  start_time: "09:00",
  end_time: "13:00",
  capacity: 8,
  price: 50,
  location: "DFW Area",
  notes: "",
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("sessions");
  const [sessions, setSessions] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [s, r, m, w] = await Promise.all([
        api.get("/admin/sessions"),
        api.get("/admin/registrations"),
        api.get("/admin/messages"),
        api.get("/admin/waitlist"),
      ]);
      setSessions(s.data);
      setRegistrations(r.data);
      setMessages(m.data);
      setWaitlist(w.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) return <div className="p-20 text-center text-gray-500">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: name === "capacity" || name === "price" ? Number(value) : value,
    }));
  };

  const submitSession = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/sessions", form);
      toast.success("Session added");
      setForm(DEFAULT_FORM);
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this session? All linked registrations will also be removed.")) return;
    try {
      await api.delete(`/admin/sessions/${id}`);
      toast.success("Session deleted");
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const deleteWaitlist = async (id) => {
    if (!window.confirm("Remove this person from the waitlist?")) return;
    try {
      await api.delete(`/admin/waitlist/${id}`);
      toast.success("Removed from waitlist");
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("rct_token");
      const res = await fetch(`${API}/admin/registrations/export.csv`, {
        credentials: "omit",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to export");
      const blob = await res.blob();
      const dispo = res.headers.get("Content-Disposition") || "";
      const match = /filename="([^"]+)"/.exec(dispo);
      const filename = match ? match[1] : "registrations.csv";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(err.message || "Export failed");
    }
  };

  const tabs = [
    { key: "sessions", label: "Availability", icon: CalIcon, count: sessions.length },
    { key: "registrations", label: "Registrations", icon: Users, count: registrations.length },
    { key: "waitlist", label: "Waitlist", icon: Hourglass, count: waitlist.length },
    { key: "messages", label: "Messages", icon: Mail, count: messages.filter((m) => !m.read).length },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="Rhythm Check Training" className="h-10 w-auto" />
            <span className="hidden md:inline font-mono-label text-gray-500">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={loadAll}
              data-testid="admin-refresh-btn"
              className="p-2 rounded-md border border-gray-300 hover:border-[#0a0a0a] transition-colors"
              title="Refresh"
            >
              <RefreshCcw className="w-4 h-4" />
            </button>
            <button
              onClick={doLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-md bg-[#0a0a0a] px-4 py-2 text-sm font-bold text-white hover:bg-[#d90429] transition-colors"
            >
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-display text-4xl md:text-5xl font-black text-[#0a0a0a]">
          Admin Dashboard
        </h1>
        <p className="mt-3 text-gray-600">Manage availability, review registrations, respond to inquiries.</p>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-bold transition-colors ${
                  tab === t.key
                    ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                    : "bg-white text-[#0a0a0a] border-gray-300 hover:border-[#0a0a0a]"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
                <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-[#d90429] text-white">{t.count}</span>
              </button>
            );
          })}
        </div>

        {tab === "sessions" && (
          <section className="mt-8 grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="font-display text-2xl font-bold text-[#0a0a0a]">Add class date</h2>
                <form onSubmit={submitSession} className="mt-5 space-y-4" data-testid="admin-add-session-form">
                  <div>
                    <label className="font-mono-label block mb-1">Class</label>
                    <select
                      name="class_slug"
                      value={form.class_slug}
                      onChange={handleFormChange}
                      required
                      data-testid="admin-form-class"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      {CLASSES.map((c) => (
                        <option key={c.slug} value={c.slug}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="font-mono-label block mb-1">Date</label>
                    <input
                      type="date"
                      name="date"
                      value={form.date}
                      onChange={handleFormChange}
                      required
                      data-testid="admin-form-date"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="font-mono-label block mb-1">Start</label>
                      <input
                        type="time"
                        name="start_time"
                        value={form.start_time}
                        onChange={handleFormChange}
                        required
                        data-testid="admin-form-start"
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="font-mono-label block mb-1">End</label>
                      <input
                        type="time"
                        name="end_time"
                        value={form.end_time}
                        onChange={handleFormChange}
                        required
                        data-testid="admin-form-end"
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="font-mono-label block mb-1">Capacity</label>
                      <input
                        type="number"
                        name="capacity"
                        min="1"
                        value={form.capacity}
                        onChange={handleFormChange}
                        required
                        data-testid="admin-form-capacity"
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="font-mono-label block mb-1">Price ($)</label>
                      <input
                        type="number"
                        name="price"
                        min="0"
                        step="1"
                        value={form.price}
                        onChange={handleFormChange}
                        data-testid="admin-form-price"
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-mono-label block mb-1">Location</label>
                    <input
                      name="location"
                      value={form.location}
                      onChange={handleFormChange}
                      required
                      data-testid="admin-form-location"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="font-mono-label block mb-1">Notes (optional)</label>
                    <textarea
                      name="notes"
                      value={form.notes}
                      onChange={handleFormChange}
                      rows="2"
                      data-testid="admin-form-notes"
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={saving}
                    data-testid="admin-add-session-submit"
                    className="w-full inline-flex justify-center items-center gap-2 rounded-md bg-[#d90429] px-5 py-3 text-sm font-bold text-white hover:bg-black transition-colors disabled:opacity-60"
                  >
                    <Plus className="w-4 h-4" />
                    {saving ? "Saving…" : "Add Session"}
                  </button>
                </form>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="font-display text-xl font-bold text-[#0a0a0a]">All scheduled sessions</h2>
                  <span className="text-sm text-gray-500">{sessions.length} total</span>
                </div>
                {sessions.length === 0 ? (
                  <div className="p-8 text-center text-gray-500" data-testid="admin-no-sessions">No sessions yet — add one on the left.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-[#f9fafb] text-left">
                        <tr>
                          <Th>Class</Th>
                          <Th>Date</Th>
                          <Th>Time</Th>
                          <Th>Seats</Th>
                          <Th>Location</Th>
                          <Th>Actions</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.map((s) => {
                          const c = CLASSES.find((x) => x.slug === s.class_slug);
                          return (
                            <tr key={s.id} className="border-t border-gray-200" data-testid={`admin-session-row-${s.id}`}>
                              <Td>
                                <div className="font-bold text-[#0a0a0a]">{c?.code || s.class_slug}</div>
                                <div className="text-xs text-gray-500 truncate max-w-[180px]">{c?.name}</div>
                              </Td>
                              <Td>{s.date}</Td>
                              <Td>{s.start_time}–{s.end_time}</Td>
                              <Td>
                                <span className={s.seats_available === 0 ? "text-[#d90429] font-bold" : ""}>
                                  {s.seats_taken}/{s.capacity}
                                </span>
                              </Td>
                              <Td>{s.location}</Td>
                              <Td>
                                <button
                                  onClick={() => deleteSession(s.id)}
                                  data-testid={`admin-delete-session-${s.id}`}
                                  className="p-1.5 border border-gray-300 rounded hover:border-[#d90429] hover:text-[#d90429]"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </Td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {tab === "registrations" && (
          <section className="mt-8">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-display text-xl font-bold text-[#0a0a0a]">Registrations</h2>
                <button
                  onClick={exportCsv}
                  disabled={registrations.length === 0}
                  data-testid="admin-export-csv-btn"
                  className="inline-flex items-center gap-2 rounded-md bg-[#0a0a0a] px-4 py-2 text-sm font-bold text-white hover:bg-[#d90429] transition-colors disabled:opacity-50"
                >
                  <Download className="w-4 h-4" /> Export CSV
                </button>
              </div>
              {registrations.length === 0 ? (
                <div className="p-8 text-center text-gray-500" data-testid="admin-no-registrations">No registrations yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-[#f9fafb] text-left">
                      <tr>
                        <Th>Registrant</Th>
                        <Th>Class</Th>
                        <Th>Date</Th>
                        <Th>Contact</Th>
                        <Th>Org</Th>
                        <Th>Code</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.map((r) => {
                        const c = CLASSES.find((x) => x.slug === r.class_slug);
                        return (
                          <tr key={r.id} className="border-t border-gray-200" data-testid={`admin-registration-row-${r.id}`}>
                            <Td>
                              <div className="font-bold text-[#0a0a0a]">{r.first_name} {r.last_name}</div>
                            </Td>
                            <Td>{c?.code || r.class_slug}</Td>
                            <Td>{r.session_date}</Td>
                            <Td>
                              <div className="text-xs">{r.email}</div>
                              <div className="text-xs text-gray-500">{r.phone}</div>
                            </Td>
                            <Td>{r.organization || "—"}</Td>
                            <Td>
                              <span className="font-mono text-xs bg-[#f9fafb] px-2 py-1 rounded">{r.confirmation_code}</span>
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}

        {tab === "waitlist" && (
          <section className="mt-8">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-display text-xl font-bold text-[#0a0a0a]">Waitlist</h2>
              </div>
              {waitlist.length === 0 ? (
                <div className="p-8 text-center text-gray-500" data-testid="admin-no-waitlist">No waitlist entries.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-[#f9fafb] text-left">
                      <tr>
                        <Th>Person</Th>
                        <Th>Class</Th>
                        <Th>Session Date</Th>
                        <Th>Contact</Th>
                        <Th>Org</Th>
                        <Th>Requested</Th>
                        <Th>Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {waitlist.map((w) => {
                        const c = CLASSES.find((x) => x.slug === w.class_slug);
                        return (
                          <tr key={w.id} className="border-t border-gray-200" data-testid={`admin-waitlist-row-${w.id}`}>
                            <Td>
                              <div className="font-bold text-[#0a0a0a]">{w.first_name} {w.last_name}</div>
                            </Td>
                            <Td>{c?.code || w.class_slug}</Td>
                            <Td>{w.session_date}</Td>
                            <Td>
                              <div className="text-xs">{w.email}</div>
                              <div className="text-xs text-gray-500">{w.phone}</div>
                            </Td>
                            <Td>{w.organization || "—"}</Td>
                            <Td className="text-xs text-gray-500">{(w.created_at || "").slice(0, 10)}</Td>
                            <Td>
                              <button
                                onClick={() => deleteWaitlist(w.id)}
                                data-testid={`admin-delete-waitlist-${w.id}`}
                                className="p-1.5 border border-gray-300 rounded hover:border-[#d90429] hover:text-[#d90429]"
                                title="Remove"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}

        {tab === "messages" && (
          <section className="mt-8 grid gap-4">
            {messages.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500" data-testid="admin-no-messages">No messages yet.</div>
            ) : (
              messages.map((m) => (
                <div key={m.id} className="bg-white border border-gray-200 rounded-lg p-6" data-testid={`admin-message-${m.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-bold text-[#0a0a0a]">{m.name}</div>
                      <div className="text-sm text-gray-600">{m.email} · {m.phone}</div>
                    </div>
                    {m.is_group_inquiry && (
                      <span className="px-2 py-1 rounded-full bg-[#d90429] text-white text-xs font-bold">GROUP</span>
                    )}
                  </div>
                  <div className="mt-4 font-display font-bold text-lg text-[#0a0a0a]">{m.subject}</div>
                  <p className="mt-2 text-gray-700 whitespace-pre-line">{m.message}</p>
                  <div className="mt-3 text-xs text-gray-500">{m.created_at}</div>
                </div>
              ))
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 font-mono-label text-gray-500 whitespace-nowrap">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 text-gray-800 align-top ${className}`}>{children}</td>;
}
